// Below types should be updated after #18 is implemented.
import type { IStekzBackendJSONResponse } from "@/wc/registry/lib/service/repository/openapi/utils";
import {
    InsufficientPropsError,
    RepositoryDoesNotSupportActionError,
} from "@/wc/registry/lib/service/repository/base.js";
import type {
    RepositoryAction,
    IRepository,
    IInputProps,
} from "@/wc/registry/lib/service/repository/types";
import type { IRepositoryParameters } from "@/wc/registry/lib/service/repository/types";

interface IFormData {
    [name: string]: unknown;
}
import "@/wc/registry/component/form-input/index.js";
import "@/wc/registry/component/confirmation-modal/index.js";
import "@/wc/registry/component/form-error-visualizer/index.js";
import "@/wc/registry/component/form-button/index.js";
import type FormButton from "@/wc/registry/component/form-button/index.js";
import "@/wc/registry/component/forms-dirty-checker/index.js";
import type { TableComponent } from "@/wc/registry/component/table/index.js";

import * as route from "@/wc/registry/lib/route/index.js";
import serviceRepository from "@/wc/registry/lib/service/repository/index.js";

import { merge, isEqual, get as lodashGet } from "lodash";
import * as dom from "@/wc/registry/lib/dom/index.js";
import * as form from "@/wc/registry/lib/form/index.js";
import * as modal from "@/wc/registry/lib/modal/index.js";
import { analytics, monitoring } from "@/lib/integrations/index";

function isFormButtonComponent(
    node: HTMLElement | FormButton
): node is FormButton {
    return node.tagName === "C-FORM-BUTTON";
}

class Form extends HTMLElement {
    setIsInitialized: (value?: unknown) => void;
    // Below promise can be used by other components to check if the
    // component and all its nested components have been fully initialized.
    isInitialized = new Promise((resolve) => {
        this.setIsInitialized = resolve;
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    actions: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _lib: any;
    _resourceRouters: HTMLElement[];

    parametersUsedForLastPrefill: IRepositoryParameters | null = null;

    formInputsInitialValues: { [key: string]: unknown } = {};

    destinationUrl: string = "";

    constructor() {
        super();
        this.actions = dom.ActionsProxy(this);
    }

    // Return a list for attributes which should be observed. When an observed
    // attribute is changed, attributeChangedCallback is called.
    static get observedAttributes() {
        // Attributes must be lower case.
        return [
            "data-extra-form-input-props",
            "data-avoid-submit-button",
            "data-show-cancel-button",
            "data-service-repository-parameters",
            "data-action",
        ];
    }

    set action(value: string) {
        this.dataset.action = value;
    }
    get action(): string {
        return this.dataset.action;
    }

    get clearFormOnSuccess() {
        if (typeof this.dataset.clearFormOnSuccess === "undefined") {
            return false;
        }
        return true;
    }

    get closeModalOnSuccess() {
        if (typeof this.dataset.closeModalOnSuccess === "undefined") {
            return false;
        }
        return true;
    }

    get avoidToastOnSubmit() {
        if (typeof this.dataset.avoidToastOnSubmit === "undefined") {
            return false;
        }
        return true;
    }

    get avoidFormDirtyCheck() {
        return typeof this.dataset.avoidFormDirtyCheck !== "undefined";
    }

    get successToastMessage() {
        return this.dataset.successToastMessage;
    }

    get failedToastMessage() {
        return this.dataset.failedToastMessage;
    }

    get avoidSettingValuesUsingRelatedFields() {
        if (
            typeof this.dataset.avoidSettingValuesUsingRelatedFields ===
            "undefined"
        ) {
            return false;
        }
        return true;
    }

    set serviceRepository(value: string) {
        this.dataset.serviceRepository = value;
    }
    get serviceRepository() {
        return this.dataset.serviceRepository;
    }

    get avoidSubmitButton() {
        if (this.dataset.avoidSubmitButton === undefined) {
            return false;
        }
        return true;
    }

    // When e.g. used in the context of inline editing in a c-table, there is
    // no need to prefill the form inputs, as the values are already retrieved
    // by the c-table we don't need to re-fetch those values.
    get skipPrefill() {
        if (this.dataset.skipPrefill === undefined) {
            return false;
        }
        return true;
    }

    get showCancelButton() {
        if (this.dataset.showCancelButton === undefined) {
            return false;
        }
        return true;
    }

    get resetFormValuesOnCancel() {
        if (this.dataset.resetFormValuesOnCancel === undefined) {
            return false;
        }
        return true;
    }

    get repository(): IRepository {
        return serviceRepository.get(this.serviceRepository);
    }

    get extraFormData(): IRepositoryParameters {
        if (
            this.dataset.extraFormData !== undefined &&
            this.dataset.extraFormData !== ""
        ) {
            return JSON.parse(this.dataset.extraFormData);
        }
        return {};
    }

    get eventName() {
        // E.g. "Updated customer information" (always use the past tense.)
        if (this.dataset.eventName) {
            return this.dataset.eventName;
        }

        // Otherwise construct one to try to describe the event that just took
        // place. Take the action (create/update/delete) and make it past tense.
        const action =
            this.action.charAt(0).toUpperCase() + this.action.slice(1) + "d";
        // E.g. Updated customers.
        return `${action} ${this.serviceRepository}`;
    }

    // Extra form input props are used for properties such as readOnly.
    get extraFormInputProps(): IInputProps {
        if (!this.dataset.extraFormInputProps) {
            return {};
        }

        return JSON.parse(this.dataset.extraFormInputProps) as IInputProps;
    }

    // Extra form inputs are only used for complex forms. They do not typically
    // fall within the range of 'normal form inputs' and require custom logic.
    //
    // E.g. a form might have a complex 'extra input' that consists of a table
    // which can be modified. This extra input might be seeded with data through
    // prefillForm.
    //
    // @TODO: Refactor this into a separate prefill-fields component - #146.
    get extraFormInputs() {
        if (!("extraFormInputs" in this.dataset)) {
            return [];
        }

        return this.dataset.extraFormInputs.split(",");
    }

    // In order to do a repository action, sometimes extra parameters are
    // required. This is backend-agnostic, but as an example we can take an
    // openapi-based repository that needs to do a POST call to
    // /customers/{customer_id}/info in order to add info for a specific
    // customer.
    //
    // The value for {customer_id} might exist in various places. It can live:
    // - in the repository context (in which case we don't need to explicitly
    //     collect and send it)
    // - on this element as an attribute
    // - on the parent c-form-generator as an attribute
    // - on a nested button as an attribute (e.g. when used in a table that
    //     supports inline-edits)
    // - on a form-input as a modifiable value (used by very few endpoints)
    //
    // All these places are inspected, and when they contain the required
    // parameter it is collected.
    getRepositoryParameters(
        action?: RepositoryAction,
        repository?: IRepository
    ): IRepositoryParameters {
        if (!action) {
            action = this.action;
        }

        if (!repository) {
            repository = this.repository;
        }

        if (!(action in repository.actionPaths)) {
            return {};
        }

        const requiredParameters =
            repository.actionPaths[action].requiredParameters;
        const parameters: IRepositoryParameters =
            dom.extractRepositoryParametersFromElement(
                this,
                requiredParameters
            );
        if (
            this.parentElement &&
            this.parentElement.tagName === "C-FORM-GENERATOR"
        ) {
            Object.assign(
                parameters,
                dom.extractRepositoryParametersFromElement(
                    this.parentElement,
                    requiredParameters
                )
            );
        }

        const button = this.getSubmitButton();
        if (button) {
            Object.assign(
                parameters,
                dom.extractRepositoryParametersFromElement(
                    button,
                    requiredParameters
                )
            );
        }

        requiredParameters.forEach((requiredParameter) => {
            // No need to search for a parameter if it already exists in the
            // repository context.
            if (requiredParameter in serviceRepository.context.get()) {
                return;
            }

            // No need to search for a parameter if it is already collected.
            if (requiredParameter in parameters) {
                return;
            }

            // Get the associated form input to potentially collect extra information
            // that is needed to execute the repository action.
            const inputElement = this.querySelector(
                `c-form-input[data-name="${requiredParameter}"]`
            ) as HTMLElementTagNameMap["c-form-input"];
            if (!inputElement) {
                return;
            }

            const value = inputElement.value;

            // Currently repository parameters are always either string or number,
            // so drop the value if it turns out to be a boolean.
            if (typeof value === "boolean") {
                return;
            }

            // Avoid forwarding the repository parameter when its value is unusable.
            if (value === undefined || value === null) {
                return;
            }

            parameters[requiredParameter] = value;
        });

        return parameters;
    }

    showConfirmationModal() {
        const confirmationModal = document.createElement(
            "c-confirmation-modal"
        );
        confirmationModal.dataset.title = "Unsaved changes";
        confirmationModal.dataset.message =
            "Do you want to leave without saving the changes?";
        confirmationModal.dataset.type = "unsaved-alert";
        confirmationModal.dataset.confirmButtonText = "Leave";
        confirmationModal.dataset.cancelButtonText = "Stay";
        this.appendChild(confirmationModal);
    }

    async handleEvent(e: Event) {
        if (e.type === "c-confirmation-modal:leave") {
            // if c-forms-dirty-checker element is found on the page, it acts as a parent
            // for all the c-forms on that page. When the user clicks on the leave button in the
            // confirmation modal, we have to reset the isDirty value to false
            // so that the user can navigate to the destination url regardless
            // of the dirty state of other child forms.
            const formsDirtyCheckerElement =
                form.getFormsDirtyCheckerElement(this);
            if (formsDirtyCheckerElement) {
                formsDirtyCheckerElement.dataset.isDirty = "false";
            }

            this.resetFormInputsInitialValues();
            route.navigate(this.destinationUrl);
            return;
        }

        if (this.getCancelButton() === e.currentTarget) {
            this.cancel();
            return;
        }

        // When a form input is either populated or changed, update depending
        // form inputs.
        if (
            ["c-form-input:populated", "c-form-input:changed"].includes(e.type)
        ) {
            e.stopPropagation();
            this.updateDependingFormInputs(
                e.target.dataset.name,
                e.target.value
            );

            const eventOptions = {};
            if (!("host" in this)) {
                eventOptions["composed"] = true;
            }
            if ("detail" in e) {
                eventOptions["detail"] = e.detail;
            }
            // Dispatch the event to the component containing this form. If the
            // parent of this form is not the shadow root, use 'composed' so the
            // event bubbles up to the parent of the shadow root.
            const parentOrHost = dom.getParentOrHost(this);
            // If the component was detached from the DOM,
            // there is no parent or host available.
            if (parentOrHost) {
                parentOrHost.dispatchEvent(
                    new CustomEvent(e.type, eventOptions)
                );
            }
            return;
        }

        const target: HTMLElement = e.currentTarget as HTMLElement;
        // Some elements still emit their 'click' events when they are disabled,
        // because the disabled attribute is only supported by <button>,
        // <command>, <fieldset>, <keygen>, <optgroup>, <option>, <select>,
        // <textarea> and <input>.
        // See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled
        //
        // So to emulate disabled behavior for our custom form buttons, we avoid
        // acting on clicks on non-supported elements. This avoids resubmitting
        // when someone clicks the form button during submission.
        if (target.hasAttribute("disabled")) {
            return;
        }
        this.submitForm();
    }

    updateDependingFormInputs(nameOfDependee: string, value: string) {
        // @TODO: Have to force the type, because typescript can't yet deal with
        // type inference from compound selectors - ticket #136
        const dependents: HTMLElementTagNameMap["c-form-input"][] = Array.from(
            this.querySelectorAll(
                `c-form-input[data-depends-on='${nameOfDependee}']`
            )
        ) as unknown as HTMLElementTagNameMap["c-form-input"][];
        dependents.forEach((dependent) => {
            // Add the key+value of the form input that is changed as a parameter
            // of the dependent form input. Which can then decide what to do
            // with it. E.g. a form-input for a producer-location-id could take
            // producer-id as a parameter, so it can fetch the
            // producer-location-id's for a specific producer id.
            dependent.setParameter(nameOfDependee.replaceAll("_", "-"), value);
            dependent.updateValues();
        });
    }

    // Since the submit button in the form can change position (e.g. when used
    // in a c-table in inline-edit mode), avoid caching the previous submit
    // button node through this.actions.submit, and use a querySelector every
    // time to select a fresh button.
    getSubmitButton():
        | HTMLButtonElement
        | HTMLElementTagNameMap["c-form-button"]
        | undefined {
        return this.querySelector('[data-proxy-action="submit"]');
    }

    getCancelButton(): HTMLButtonElement | undefined {
        return this.querySelector('[data-proxy-action="cancel"]');
    }

    // Update the visual appearance of the submit button.
    updateSubmitButton(state: "loading" | "" = "") {
        const submitButton = this.getSubmitButton();
        if (this.avoidSubmitButton) {
            return;
        }
        const isLoading = state === "loading";

        // Always disable the submit button while loading, and add a 'loading'
        // class to the button so its style can be customized further if needed.
        if (isLoading) {
            submitButton.setAttribute("disabled", "");
            submitButton.classList.add(state);
        } else {
            submitButton.removeAttribute("disabled");
            submitButton.classList.remove("loading");
        }

        if (isFormButtonComponent(submitButton)) {
            submitButton.loading = isLoading;
        }
    }

    async getFormData(extraFormData: IFormData = {}) {
        return merge(extraFormData, await form.getFormDataFromElement(this));
    }

    clearForm() {
        this.querySelectorAll("c-form-input").forEach((element) => {
            element.value = "";
            if (element.dataset.default) {
                element.value = element.dataset.default;
            }
        });
    }

    async submitForm(
        targetRepositoryName?: string,
        extraFormData: IFormData = {}
    ): Promise<boolean> {
        this.updateSubmitButton("loading");

        let repository = this.repository;
        if (targetRepositoryName) {
            repository = serviceRepository.get(targetRepositoryName);
        }

        // Get formData from form inputs and nested form inputs - also merge
        // in extra form data that might be passed to this function.
        const formData = await this.getFormData(extraFormData);
        this.resetErrors();
        let response: Response, json: IStekzBackendJSONResponse;
        try {
            response = await repository.objects[this.action](
                this.getRepositoryParameters(
                    this.action as RepositoryAction,
                    repository
                ),
                {
                    ...formData,
                    ...this.extraFormData,
                }
            );
        } catch (e) {
            if (e instanceof TypeError && e.message.includes("NetworkError")) {
                // Show a general error indicating that we can't connect
                // to the backend.
                this.showErrorsOnFormErrorVisualizerIfRequired({
                    json: { errors: ["Can't connect to the backend."] },
                    response: undefined,
                });
                return;
            } else {
                throw e;
            }
        }
        try {
            json = await response.json();
        } catch (e) {
            // When there is a SyntaxError, likely the response doesn't include a correct json body
            if (!(e instanceof SyntaxError)) {
                throw e;
            }
        } finally {
            this.updateSubmitButton();
        }

        // Changing some error messages to make it clearer what is happening.
        if (
            json &&
            "detail" in json &&
            typeof json["detail"] === "string" &&
            json["detail"] === "Invalid token format"
        ) {
            json["detail"] = "You are not authenticated yet.";
        }

        // Parse json from response once before passing it to consuming elements,
        // since it can only be done once.
        if (json) {
            this.showErrorsOnFormErrorVisualizerIfRequired({ response, json });
            this.showErrorsOnFormInputsIfRequired(json);
        }

        if ([200, 201, 204].includes(response.status)) {
            if (this.clearFormOnSuccess) {
                this.clearForm();
            }

            this.resetFormInputsInitialValues(true);

            // when the form is successfully submitted, remove the isDirty attribute
            // from the forms-dirty-checker element
            const formsDirtyCheckerElement =
                form.getFormsDirtyCheckerElement(this);
            if (formsDirtyCheckerElement) {
                delete formsDirtyCheckerElement.dataset.isDirty;
            }

            // close the modal if the response status is 200 or 201
            if (this.closeModalOnSuccess) {
                modal.closeSurroundingModal(this);
            }

            analytics.track(this.eventName);

            const navigateTo = this.dataset.navigateToOnSuccess;
            if (navigateTo) {
                route.navigate(navigateTo);
            }
            return true;
        } else {
            // E.g. Update customers - note, no past tense as it hasn't happened.
            const action =
                this.action.charAt(0).toUpperCase() + this.action.slice(1);
            analytics.track("Form contains validation errors", {
                action: `${action} ${this.serviceRepository}`,
            });
        }

        return false;
    }

    // Reset all the errors that might be showing. This function should be
    // called before a request, so that the user can see that something is
    // happening.
    resetErrors() {
        const errorVisualiser = this.querySelector("c-form-error-visualizer");
        if (errorVisualiser) {
            errorVisualiser.reset();
        }
        // Each form input might be showing an error message. Calling reset()
        // on these nodes removes these error messages.
        [...this.querySelectorAll("c-form-input")].forEach((node) => {
            node.reset();
        });
    }

    // When there is a form visualiser, pass the response along to the form
    // visualizer, which can decide what to do with it.
    showErrorsOnFormErrorVisualizerIfRequired({
        response,
        json,
    }: {
        response: Response;
        json: IStekzBackendJSONResponse;
    }) {
        // If this form has a visualizer, pass the response and json.
        const errorVisualiser = this.querySelector("c-form-error-visualizer");
        if (errorVisualiser) {
            errorVisualiser.data = { response, json };
        }
    }

    // Extract fields mentioned in the json, and map these fields to form
    // inputs. Update the error that belong to these form inputs, so that
    // they can decide what to do with it. (e.g. show a help text and / or
    // activate some error indicators)
    showErrorsOnFormInputsIfRequired(json: IStekzBackendJSONResponse) {
        if ("detail" in json && typeof json["detail"] !== "string") {
            json["detail"].forEach((detail) => {
                if ("msg" in detail && "loc" in detail) {
                    // detail["loc"] might contain ["body", "contact_detail", "email"].
                    // We can ignore 'body' (since we know that we're talking about
                    // data that we sent through the request body). Then, when there
                    // are more than 1 fields, it is talking about an error in a
                    // nested field. In case of a nested field, the errorLocation
                    // might become something "contact_details.name" so that we're
                    // able to match it to a specific form input.

                    // when the BE schema contains a discriminator, pydantic adds the discriminated
                    // schema name in the list of loc which is not needed for us to target the
                    // error location, therefore remove it from the list
                    // #issue - https://github.com/pydantic/pydantic/issues/3947#issuecomment-1527683116
                    const fieldsWithoutCapitalLetters = detail["loc"].filter(
                        (field) => this.containsCapitalLetter(field) === false
                    );
                    const errorLocation = fieldsWithoutCapitalLetters
                        .slice(1)
                        .join(".");
                    const selector = `c-form-input[data-name='${errorLocation}']`;

                    // @TODO: Have to force the type, because typescript can't yet deal with
                    // type inference from compound selectors - ticket #136
                    let relatedFormInput: HTMLElementTagNameMap["c-form-input"] =
                        this.querySelector(
                            selector
                        ) as unknown as HTMLElementTagNameMap["c-form-input"];

                    // Fall back to 'id' if the related form input could not be found.
                    if (!relatedFormInput) {
                        relatedFormInput = this.querySelector(
                            `c-form-input#${errorLocation}`
                        ) as HTMLElementTagNameMap["c-form-input"];
                    }

                    if (relatedFormInput) {
                        relatedFormInput.error = detail.msg;
                    }
                }
            });
        }
    }

    containsCapitalLetter(field: string): boolean {
        return /[A-Z]/.test(field);
    }

    // Checks whether all required attributes can be found on this element
    // or in its context in the case of the resource ID. When they are not,
    // an error will be thrown. The goal of this function is to make it easier
    // for the developer to know what they missed when using this component
    // in a template.
    isConfigured() {
        if (!this.dataset.serviceRepository) {
            throw Error(
                `'${this.tagName}' requires the 'data-service-repository' attribute to be configured`
            );
        }
        if (!this.repository) {
            throw Error(
                `'${this.tagName}' requires the 'data-service-repository' to point to an actual repository`
            );
        }

        if (this.avoidSubmitButton) {
            return;
        }
        const serviceRepositories = this.serviceRepository.split(",");
        if (serviceRepositories.length === 1) {
            if (!this.getSubmitButton()) {
                throw Error(
                    `'${this.tagName}' requires a child node with attribute 'data-proxy-action="submit"'`
                );
            }
        } else {
            serviceRepositories.forEach((repoName) => {
                const submitButton = this.actions[`submit\\:${repoName}`];
                if (!submitButton) {
                    throw Error(
                        `'${this.tagName}' requires a child node with attribute 'data-proxy-action="submit:${repoName}"'`
                    );
                }
            });
        }

        // Test if this component has the right parameters for the repository
        // action to work.
        try {
            this.repository.objects.test(
                this.action,
                this.getRepositoryParameters(this.action, this.repository)
            );
        } catch (e) {
            if (e instanceof InsufficientPropsError) {
                const requiredAttribute = e.missingProp.replaceAll("_", "-");
                throw Error(
                    `One of these should be applicable:\n1. This element has the '${requiredAttribute}' attribute set.\n2. A c-form-generator element in which this element is nested has the '${requiredAttribute}' attribute set.\n3. The '${e.missingProp}' property exists in the repository context.`
                );
            }
        }
    }

    getPrefillRepository() {
        if ("serviceRepositoryForPrefill" in this.dataset) {
            return serviceRepository.get(
                this.dataset.serviceRepositoryForPrefill
            );
        }
        return this.repository;
    }

    getValueFromJSON(json: object, fieldLocator: string) {
        const value = lodashGet(json, fieldLocator);
        if (value) {
            return value;
        }
        if (typeof value === "boolean") {
            return value;
        }

        // There might be still a chance the value is present in a related
        // field. E.g. a customer_id might exist in customer.id. This behavior
        // is enabled by default but can be disabled through
        // data-avoid-setting-values-using-related-fields.
        if (
            !this.avoidSettingValuesUsingRelatedFields &&
            fieldLocator.endsWith("_id")
        ) {
            return lodashGet(json, fieldLocator.replace("_id", ".id"));
        }
    }

    // Sets the values of the meta keys to the form input as a stringified json
    // in data-meta-entries attribute. This is useful only if the value of the form-input
    // is an object and if we want to extract the key-value pairs of the object to prefill
    // the form input or to perform some other action.
    // E.g. you can set the data-meta-keys attribute to "name,description" to a form-input
    // which contains items object as its value, and as a result the data-meta-entries attribute
    //  will be set to {"name": "butter", "description": "dairy product"}
    setMetaEntriesToFormInput(
        json: object,
        fieldLocator: string,
        formInput:
            | HTMLElementTagNameMap["c-form-input"]
            | TableComponent
            | HTMLElement
    ) {
        const metaKeys = formInput.dataset.metaKeys?.split(",") || [
            formInput.dataset.keyDescription,
        ];
        const metaEntries = {};

        let value = lodashGet(json, fieldLocator);

        // There might be still a chance the values of the meta-keys are presented in a related
        // field. E.g. if the field is item_id, then there might also be a field as item
        // which is an object containing the meta-keys.
        if (fieldLocator.endsWith("_id")) {
            value = lodashGet(json, fieldLocator.replace("_id", ""));
        }

        metaKeys.forEach((key) => {
            metaEntries[key] = lodashGet(value, key);
        });

        formInput.dataset.metaEntries = JSON.stringify(metaEntries);
    }

    // When a update action is configured on the form, it means we are changing
    // an existing entity.
    // Since the repository is configured on this element, we can check if
    // we can do a GET on the existing entity to pre-fill these values in
    // our form.
    async prefillValuesIfRequired(
        data = null,
        silenceErrors = false
    ): Promise<object | undefined> {
        if (
            this.action !== "update" &&
            this.action !== "partialUpdate" &&
            !("serviceRepositoryForPrefill" in this.dataset)
        ) {
            return;
        }

        if (this.skipPrefill) {
            return;
        }

        let json = data || {};
        if (!data) {
            const prefillRepository = this.getPrefillRepository();

            let action = "get";
            // If a given repository only supports one type of action, use that
            // action instead. This is useful to support openapi-generated
            // repositories where the endpoints are not crud-like.
            if (Object.keys(prefillRepository.actionPaths).length === 1) {
                action = Object.keys(prefillRepository.actionPaths)[0];
            }

            const params = this.getRepositoryParameters(
                action as RepositoryAction,
                prefillRepository
            );

            this.parametersUsedForLastPrefill = params;

            // when data-service-repository-parameters and data-action properties change,
            // the prefillValuesIfRequired function is called twice. The first time
            // it is called, the repository might not be ready yet, therefore silence
            // any error that might be thrown.
            let errorsThrown = false;
            try {
                prefillRepository.objects.test(action, params);
            } catch (e) {
                if (e instanceof RepositoryDoesNotSupportActionError) {
                    monitoring.error(
                        `Cannot pre-fill form, as there is no 'get' method available in the '${this.serviceRepository}' repository.`
                    );
                    return;
                }
                errorsThrown = true;
                if (silenceErrors) {
                    return;
                }
                throw e;
            }

            if (errorsThrown) {
                return;
            }

            const response = await prefillRepository.objects[action](params);
            json = await response.json();
        }

        const formInputs = this.collectAllFormInputs();

        formInputs.forEach((formInput) => {
            if (!formInput.dataset.name) {
                return;
            }

            // in some rare cases we want to avoid prefilling extra-form-input
            // because we want to prefill through a different component
            if (formInput.dataset.skipPrefill !== undefined) {
                return;
            }

            const fieldLocator = formInput.dataset.name;
            let value = this.getValueFromJSON(json, fieldLocator);
            if (value === null || value === undefined) {
                return;
            }

            if (typeof value === "object") {
                value = JSON.stringify(value);
            }

            formInput.dataset.value = `${value}`;

            if ("getNestedInputComponent" in formInput) {
                const nestedInputComponent =
                    formInput.getNestedInputComponent();
                if (
                    nestedInputComponent &&
                    "setIsPrefilled" in nestedInputComponent
                ) {
                    // resolve the isPrefilled promise to indicate that the form input has been prefilled
                    nestedInputComponent.setIsPrefilled();
                }
            }

            // set the values of the meta keys to the form input metaEntries dataset as a stringified json
            // if the input type is autocomplete-choice
            if (formInput.dataset.type === "autocomplete-choice") {
                this.setMetaEntriesToFormInput(json, fieldLocator, formInput);
            }
        });
        return json;
    }

    collectAllFormInputs(): (
        | HTMLElementTagNameMap["c-form-input"]
        | TableComponent
        | HTMLElement
    )[] {
        const formInputs = [
            ...this.querySelectorAll("c-form-input"),
            ...this.extraFormInputs
                .map((name) => [...this.querySelectorAll(name)])
                .flat(),
        ];

        const tablesInForm: TableComponent[] = [
            ...this.querySelectorAll("c-table"),
        ];
        tablesInForm.forEach((table) => {
            if (!table.dataset.name) {
                return;
            }

            formInputs.push(table);
        });
        return formInputs;
    }

    async updateDependingFormInputsIfRequired() {
        const dependents: HTMLElementTagNameMap["c-form-input"][] = Array.from(
            this.querySelectorAll(`c-form-input[data-depends-on]`)
        ) as unknown as HTMLElementTagNameMap["c-form-input"][];
        dependents.forEach((dependent) => {
            const nameOfDependee = dependent.dataset.dependsOn;
            const dependee = this.querySelector(
                `c-form-input[data-name='${nameOfDependee}']`
            );
            if (!dependee) {
                return;
            }

            const value = dependee.value;
            if (value === null || value === undefined) {
                return;
            }

            dependent.setParameter(nameOfDependee.replaceAll("_", "-"), value);
            dependent.updateValues();
        });
    }

    async processExtraFormInputPropsIfRequired(oldValues?: string) {
        const oldValueToProcess = oldValues ?? "{}";
        const isProcessingRequired =
            !!this.dataset.extraFormInputProps ||
            (oldValueToProcess &&
                oldValueToProcess !== this.dataset.extraFormInputProps);

        if (!isProcessingRequired) {
            return;
        }

        // extraFormInputProps must be camelCase.
        const currentExtraFormInputProps = { ...this.extraFormInputProps };

        const valuesToRemove = Object.keys(
            JSON.parse(oldValueToProcess)
        ).filter(
            // For each old value to process
            (key) => {
                // If there are no extraFormInputProps, then old value needs to be removed from formInputs.
                // OR
                // If old value not in currentExtraFormInputProps, then old value needs to be removed from formInputs.
                if (
                    !this.dataset.extraFormInputProps ||
                    !(key in currentExtraFormInputProps)
                ) {
                    return true;
                }
                // Else don't remove.
                return false;
            }
        );

        valuesToRemove.forEach((valueToRemove) => {
            this.querySelectorAll("c-form-input").forEach((formInput) => {
                // Not necessary to check if item to delete exists because
                // Evaluating the delete operator on an object property that
                // does not exist does not produce an error.
                // The operator simply returns true.
                delete formInput.dataset[valueToRemove];
            });
        });

        // Add in any new properties.
        if (!currentExtraFormInputProps) {
            return;
        }
        Object.entries(currentExtraFormInputProps).forEach(([key, value]) => {
            this.querySelectorAll("c-form-input").forEach((formInput) => {
                // dataset value must be a string.
                formInput.dataset[key] = value.toString();
            });
        });
    }

    // On click of cancel, either reset or clear values.
    async cancel() {
        if (this.resetFormValuesOnCancel) {
            // On cancel, if resetFormValuesOnCancel is true, then repopulate instead of clearing.
            await this.prefillValuesIfRequired();
        } else {
            // Otherwise, clear out values.
            this.querySelectorAll("c-form-input").forEach((element) => {
                element.value = "";
                if (element.dataset.default) {
                    element.value = element.dataset.default;
                }
            });
        }

        this.resetFormInputsInitialValues();
        this.dispatchEvent(
            new CustomEvent("c-form:canceled", {
                bubbles: true,
                composed: true,
            })
        );
    }

    async initializeFormInputs() {
        // Make sure that the value getter from c-form-input can be used.
        await customElements.whenDefined("c-form-input");

        const promises = Array.from(this.querySelectorAll("c-form-input")).map(
            (element) => element.waitUntilInitialized()
        );

        await Promise.all(promises);

        // Instead of letting the component error out somewhere during its
        // lifetime, pre-check if all required conditions have been fulfilled.
        // That way we can guarantee that is will function correctly.
        this.isConfigured();

        const json = await this.prefillValuesIfRequired();
        await this.processExtraFormInputPropsIfRequired();
        await this.updateDependingFormInputsIfRequired();

        // To avoid blocking future prefills whenever the
        // data-service-repository-parameters change.
        if (this.parametersUsedForLastPrefill === null) {
            this.parametersUsedForLastPrefill = {};
        }

        this.setIsInitialized(json);
        this.resetFormInputsInitialValues();
    }

    // When the value attribute is changed, be sure to update the nested nodes
    // to reflect this change.
    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === "data-extra-form-input-props") {
            this.processExtraFormInputPropsIfRequired(oldValue);
            return;
        }

        if (name === "data-avoid-submit-button" && newValue !== oldValue) {
            const submitButton = this.getSubmitButton();
            if (!submitButton) {
                return;
            }

            if (this.avoidSubmitButton) {
                submitButton.removeEventListener("click", this);
            } else {
                submitButton.addEventListener("click", this);
            }
            return;
        }

        if (name === "data-show-cancel-button" && newValue !== oldValue) {
            const cancelButton = this.getCancelButton();
            if (!cancelButton) {
                return;
            }

            if (this.showCancelButton) {
                cancelButton.addEventListener("click", this);
            } else {
                cancelButton.removeEventListener("click", this);
            }
            return;
        }

        if (
            (name === "data-service-repository-parameters" &&
                newValue !== oldValue) ||
            (name === "data-action" && newValue !== "create")
        ) {
            // When the value is null, the last prefill hasn't taken place yet because
            // the component is still initializing. In that case, we don't have
            // to do anything as it will happen as a part of the initialization.
            if (this.parametersUsedForLastPrefill === null) {
                return;
            }
            // Clear the form as the keys for the form-inputs for the upcoming prefill
            // request might not have 100% overlap with the previously set values.
            this.clearForm();
            this.resetErrors();
            this.prefillValuesIfRequired(null, true);
            return;
        }
    }

    checkForFormInputIsClean(pathname: string): boolean {
        if (this.avoidFormDirtyCheck) {
            return true;
        }

        // if a page has multiple forms, and if some or all of the forms on that page are dirty,
        // we prevent the user from navigating to a different page by showing a confirmation modal.
        // But since the validation is done on the form level, we will endup getting multiple confirmation modals
        // for each form that is dirty. To avoid this, we set the isDirty value to false on the c-forms-dirty-checker
        // element when the user clicks on the leave button on the first confirmation modal,
        // and we determine the next unsaved changes alert based on the isDirty value.
        const formsDirtyCheckerElement = form.getFormsDirtyCheckerElement(this);
        if (
            formsDirtyCheckerElement &&
            formsDirtyCheckerElement.dataset.isDirty === "false"
        ) {
            // allow the user to navigate to the destination url, since at least one form is clean
            return true;
        }

        const updatedData: { [key: string]: unknown } = {};
        const formInputs = this.collectAllFormInputs();
        formInputs.forEach((formInput) => {
            if (!formInput.dataset.name) {
                return;
            }
            // in some rare cases we want to avoid prefilling extra-form-input
            // because we want to prefill through a different component
            if (formInput.dataset.skipPrefill !== undefined) {
                return;
            }
            const fieldLocator = formInput.dataset.name;
            let value = formInput.dataset.value ?? null;
            if ("getNestedInputComponent" in formInput) {
                const nestedInputComponent =
                    formInput.getNestedInputComponent();
                value = nestedInputComponent?.value ?? null;
            }
            updatedData[fieldLocator] = value;
        });

        const isFormDirty = !isEqual(this.formInputsInitialValues, updatedData);

        if (isFormDirty) {
            if (formsDirtyCheckerElement) {
                formsDirtyCheckerElement.dataset.isDirty = "true";
            }
            this.destinationUrl = pathname;
            this.showConfirmationModal();
            return false;
        } else {
            return true;
        }
    }

    resetFormInputsInitialValues(isFormSaved = false) {
        // the initial values of the formInputs on the tables will have different values
        // from when they are initialized vs when they are saved. So, we need to reset
        // the initial values when the form is saved.
        if (
            isFormSaved &&
            this.parentNode &&
            this.parentNode.host &&
            this.parentNode.host.tagName === "C-TABLE"
        ) {
            this.formInputsInitialValues = {};
            return;
        }
        const formInputs = this.collectAllFormInputs();
        formInputs.forEach((formInput) => {
            if (!formInput.dataset.name) {
                return;
            }
            // in some rare cases we want to avoid prefilling extra-form-input
            // because we want to prefill through a different component
            if (formInput.dataset.skipPrefill !== undefined) {
                return;
            }
            const fieldLocator = formInput.dataset.name;
            let value = formInput.dataset.value ?? null;
            if ("getNestedInputComponent" in formInput) {
                const nestedInputComponent =
                    formInput.getNestedInputComponent();
                value = nestedInputComponent?.value ?? null;
            }
            this.formInputsInitialValues[fieldLocator] = value;
        });
    }

    async connectedCallback() {
        await this.initializeFormInputs();

        this.addEventListener("c-form-input:populated", this);
        this.addEventListener("c-form-input:changed", this);
        if (!this.avoidSubmitButton) {
            this.getSubmitButton().addEventListener("click", this);
        }

        if (this.showCancelButton) {
            this.getCancelButton().addEventListener("click", this);
        }
        this.addEventListener("c-confirmation-modal:leave", this);
    }

    disconnectedCallback() {
        this.removeEventListener("c-form-input:populated", this);
        this.removeEventListener("c-form-input:changed", this);

        if (!this.avoidSubmitButton) {
            this.getSubmitButton().removeEventListener("click", this);
        }

        if (this.showCancelButton) {
            this.getCancelButton().removeEventListener("click", this);
        }
        this.removeEventListener("c-confirmation-modal:leave", this);

        this.resetFormInputsInitialValues();
    }
}

customElements.define("c-form", Form);

declare global {
    interface HTMLElementTagNameMap {
        "c-form": Form;
    }
}
